import { supabase } from '../lib/supabase';

export interface CreateGiftCardData {
  business_id: string;
  design_id: string;
  amount: number;
  purchaser_customer_id: string; // Link to customer (required)
  recipient_name?: string;
  recipient_email?: string;
  custom_message?: string;
  notes?: string;
  promotion_id?: string;
  bonus_amount?: number;
}

export class GiftCardService {
  /**
   * Generate a unique gift card code
   * Format: GIFT-XXXX-XXXX-XXXX
   */
  static generateCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const segments = 3;
    const segmentLength = 4;
    
    const parts = [];
    for (let i = 0; i < segments; i++) {
      let segment = '';
      for (let j = 0; j < segmentLength; j++) {
        segment += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      parts.push(segment);
    }
    
    return `GIFT-${parts.join('-')}`;
  }

  /**
   * Check if a gift card code is unique for a business
   */
  static async isCodeUnique(businessId: string, code: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('gift_cards')
        .select('id')
        .eq('business_id', businessId)
        .eq('code', code)
        .single();

      if (error && error.code === 'PGRST116') {
        // No record found - code is unique
        return true;
      }

      // If data exists, code is not unique
      return !data;
    } catch (error) {
      console.error('Error checking code uniqueness:', error);
      return false;
    }
  }

  /**
   * Generate a guaranteed unique code for a business
   */
  static async generateUniqueCode(businessId: string): Promise<string> {
    let attempts = 0;
    const maxAttempts = 10;

    while (attempts < maxAttempts) {
      const code = this.generateCode();
      const isUnique = await this.isCodeUnique(businessId, code);
      
      if (isUnique) {
        return code;
      }
      
      attempts++;
    }

    throw new Error('Failed to generate unique gift card code after multiple attempts');
  }

  /**
   * Create a new gift card
   */
  static async createGiftCard(data: CreateGiftCardData): Promise<{ success: boolean; giftCard?: any; error?: string }> {
    try {
      console.log('🎁 Creating gift card...', data);

      // Generate unique code
      const code = await this.generateUniqueCode(data.business_id);
      console.log('✨ Generated code:', code);

      // Calculate total amount with bonus
      const totalAmount = data.amount + (data.bonus_amount || 0);

      // Create gift card record
      const { data: giftCard, error: cardError } = await supabase
        .from('gift_cards')
        .insert({
          business_id: data.business_id,
          code: code,
          initial_amount: data.amount,
          current_balance: totalAmount,
          purchaser_customer_id: data.purchaser_customer_id,
          recipient_name: data.recipient_name,
          recipient_email: data.recipient_email,
          promotion_id: data.promotion_id,
          bonus_amount: data.bonus_amount || 0,
          status: 'active',
          expires_at: null, // Never expire for now
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (cardError) {
        console.error('❌ Error creating gift card:', cardError);
        throw cardError;
      }

      console.log('✅ Gift card created:', giftCard);

      // Create purchase transaction
      const transactionNotes = data.notes || `Gift card purchased${data.recipient_name ? ` for ${data.recipient_name}` : ''}${data.bonus_amount ? ` (includes $${data.bonus_amount.toFixed(2)} promotional bonus)` : ''}`;
      
      const { error: transactionError } = await supabase
        .from('gift_card_transactions')
        .insert({
          gift_card_id: giftCard.id,
          transaction_type: 'purchase',
          amount: data.amount,
          balance_before: 0,
          balance_after: totalAmount,
          notes: transactionNotes
        });

      if (transactionError) {
        console.error('❌ Error creating transaction:', transactionError);
        throw transactionError;
      }

      console.log('✅ Purchase transaction created');

      return {
        success: true,
        giftCard
      };
    } catch (error) {
      console.error('Error creating gift card:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create gift card'
      };
    }
  }

  /**
   * Get gift card by code for a specific business
   */
  static async getGiftCardByCode(businessId: string, code: string): Promise<any | null> {
    try {
      const { data, error } = await supabase
        .from('gift_cards')
        .select('*')
        .eq('business_id', businessId)
        .eq('code', code.toUpperCase().trim())
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Not found
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching gift card:', error);
      return null;
    }
  }

  /**
   * Validate a gift card for redemption
   */
  static async validateForRedemption(businessId: string, code: string): Promise<{
    isValid: boolean;
    giftCard?: any;
    error?: string;
  }> {
    const giftCard = await this.getGiftCardByCode(businessId, code);

    if (!giftCard) {
      return {
        isValid: false,
        error: 'Gift card not found'
      };
    }

    if (giftCard.status === 'expired') {
      return {
        isValid: false,
        giftCard,
        error: 'This gift card has expired'
      };
    }

    if (giftCard.status === 'redeemed' || giftCard.current_balance <= 0) {
      return {
        isValid: false,
        giftCard,
        error: 'This gift card has been fully redeemed'
      };
    }

    return {
      isValid: true,
      giftCard
    };
  }

  /**
   * Redeem a gift card for a booking payment
   */
  static async redeemGiftCard(
    businessId: string,
    giftCardId: string,
    bookingId: string,
    redemptionAmount: number
  ): Promise<{
    success: boolean;
    giftCard?: any;
    transaction?: any;
    error?: string;
  }> {
    try {
      console.log('🎁 Redeeming gift card...', {
        businessId,
        giftCardId,
        bookingId,
        redemptionAmount
      });

      // Get the gift card
      const { data: giftCard, error: cardError } = await supabase
        .from('gift_cards')
        .select('*')
        .eq('id', giftCardId)
        .eq('business_id', businessId)
        .single();

      if (cardError || !giftCard) {
        console.error('❌ Gift card not found:', cardError);
        return {
          success: false,
          error: 'Gift card not found'
        };
      }

      // Validate gift card
      const validation = await this.validateForRedemption(businessId, giftCard.code);
      if (!validation.isValid) {
        return {
          success: false,
          error: validation.error || 'Gift card is not valid for redemption'
        };
      }

      // Validate redemption amount
      if (redemptionAmount <= 0) {
        return {
          success: false,
          error: 'Redemption amount must be greater than 0'
        };
      }

      if (redemptionAmount > giftCard.current_balance) {
        return {
          success: false,
          error: `Redemption amount ($${redemptionAmount.toFixed(2)}) exceeds gift card balance ($${giftCard.current_balance.toFixed(2)})`
        };
      }

      const balanceBefore = giftCard.current_balance;
      const balanceAfter = balanceBefore - redemptionAmount;

      // Use a transaction to ensure atomicity
      // First, create the redemption transaction
      const { data: transaction, error: transactionError } = await supabase
        .from('gift_card_transactions')
        .insert({
          gift_card_id: giftCardId,
          booking_id: bookingId,
          transaction_type: 'redemption',
          amount: redemptionAmount,
          balance_before: balanceBefore,
          balance_after: balanceAfter,
          notes: `Redeemed for booking payment`
        })
        .select()
        .single();

      if (transactionError) {
        console.error('❌ Error creating redemption transaction:', transactionError);
        return {
          success: false,
          error: 'Failed to create redemption transaction'
        };
      }

      // Update gift card balance
      const { error: updateError } = await supabase
        .from('gift_cards')
        .update({
          current_balance: balanceAfter,
          updated_at: new Date().toISOString()
        })
        .eq('id', giftCardId);

      if (updateError) {
        console.error('❌ Error updating gift card balance:', updateError);
        // Try to rollback the transaction (delete it)
        await supabase
          .from('gift_card_transactions')
          .delete()
          .eq('id', transaction.id);
        
        return {
          success: false,
          error: 'Failed to update gift card balance'
        };
      }

      // Get updated gift card
      const { data: updatedGiftCard } = await supabase
        .from('gift_cards')
        .select('*')
        .eq('id', giftCardId)
        .single();

      console.log('✅ Gift card redeemed successfully');
      return {
        success: true,
        giftCard: updatedGiftCard,
        transaction
      };
    } catch (error) {
      console.error('Error redeeming gift card:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to redeem gift card'
      };
    }
  }
}

